import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { describeApiError, reportApiError } from '@/common/utils/apiError';

/**
 * Metadata opcional de cada query/mutation. `operation` es lo que aparece como
 * tag en Sentry, así que conviene que describa la acción en términos del
 * dominio ("kanban:columna", "preferencias:guardar").
 */
export interface QueryMeta {
  operation?: string;
  /** No reportar ni avisar: la pantalla ya maneja este error por su cuenta */
  silent?: boolean;
}

const metaOf = (meta: unknown): QueryMeta => (meta ?? {}) as QueryMeta;

/**
 * Un 429 es transversal: si se rompe una columna del kanban, se rompen las
 * cuatro. Se avisa una sola vez con un id fijo para no apilar cuatro toasts
 * idénticos.
 */
const RATE_LIMIT_TOAST_ID = 'api-rate-limited';

const notifyIfCrossCutting = (error: unknown) => {
  const info = describeApiError(error);

  if (info.isRateLimited) {
    toast.error(info.message, { id: RATE_LIMIT_TOAST_ID, duration: 6000 });
  }
};

const retryPolicy = (failureCount: number, error: unknown): boolean => {
  const info = describeApiError(error);

  // Reintentar un 429 lo empeora: consume más del cupo que ya se agotó.
  if (info.isCanceled || info.isAuth || info.isRateLimited) return false;

  // El resto de los 4xx no se arregla repitiendo la misma request.
  if (info.status !== null && info.status < 500 && info.status !== 408) return false;

  return failureCount < 2;
};

export const createAppQueryClient = (): QueryClient =>
  new QueryClient({
    // Hasta acá ninguna query fallida llegaba a Sentry: los errores morían en el
    // cartel rojo de cada pantalla y no quedaba registro de la causa.
    queryCache: new QueryCache({
      onError: (error, query) => {
        const meta = metaOf(query.meta);
        if (meta.silent) return;

        reportApiError(error, {
          operation: meta.operation ?? 'query',
          extra: { queryKey: query.queryKey },
        });

        notifyIfCrossCutting(error);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        const meta = metaOf(mutation.meta);
        if (meta.silent) return;

        reportApiError(error, {
          operation: meta.operation ?? 'mutation',
          extra: { mutationKey: mutation.options.mutationKey },
        });

        notifyIfCrossCutting(error);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: retryPolicy,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
  });
